import { DBBotDeploymentThunks, DBBotThunks, botSelectByQuery, resetAutomationSessionState, resetBotDeploymentState } from '@common-reducers/DBServiceThunks';
import { selectOnlineEngines } from '@common/reducers/ConnectedEnginesSlice';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { Bot } from '@common/models/bot';
import { setCurrentBot, setIsReadyToLoadFlow } from '@common/reducers/ChatbotReducer';
import { createAndReRouteToNewBotEditorThunk, toggleBotActivationThunk } from '@common/reducers/ChatbotThunks';
import { type ClassValue, clsx } from "clsx";
import { Bot as BotIcon, Calendar, ChevronDown, Edit, Loader2, Plus, Search, Trash2, Wifi, WifiOff } from 'lucide-react';
import React, { memo, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { twMerge } from "tailwind-merge";
import { useCalculateBotStatsMutation } from '@common/reducers/backend-api/backend-api';
// Deep-import the credit widgets instead of the credits-usage barrel: the barrel
// re-exports credits-usage.tsx (recharts + d3 + victory-vendor), and with
// tree-shaking off in the extension build a barrel import pulls that whole chart
// cluster into app.js even though the chart is website-only. See
// PERF_AUDIT_PROGRESS.md Round 12.
import { CreditWarningAlert } from '@common/components/credits-usage/credit-warning-alert';
import AddCreditsModal from '@common/components/credits-usage/add-credits-modal';
import { selectCreditsData, selectIsLoading as selectCreditUsageIsLoading, fetchCreditUsage } from '@common-reducers/CreditUsageReducer';
import BotStatusComponent from '@common/components/bot-status/bot-status';
import { AnalyticsService } from '@common/services/analytics-service';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common/config/constants';

// Utility function for merging class names
function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

// Safe access helper function for credit data
const getValueOrDefault = (data: any, path: string, defaultValue: any) => {
    if (!data) return defaultValue;
    
    const keys = path.split('.');
    let value = data;
    
    for (const key of keys) {
        if (value === undefined || value === null) return defaultValue;
        value = value[key];
    }
    
    return value === undefined || value === null ? defaultValue : value;
};

// Memoized search input component to prevent losing focus
const SearchInput = memo(({
    value,
    onChange,
    placeholder
}: {
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    placeholder: string;
}) => {
    return (
        <div className="relative w-full md:w-[600px] md:flex-auto">
            <Search 
                className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 pointer-events-none" 
                style={{ color: 'var(--color-text-muted)' }}
            />
            <input
                type="text"
                placeholder={placeholder}
                value={value}
                onChange={onChange}
                style={{
                    width: '100%',
                    height: '48px',
                    paddingLeft: '48px',
                    paddingRight: '24px',
                    backgroundColor: 'var(--color-surface)',
                    border: '1px solid var(--color-border)',
                    borderRadius: '12px',
                    fontSize: '1.125rem',
                    color: 'var(--color-text)',
                    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                    transition: 'all 200ms',
                }}
                onFocus={(e) => {
                    e.target.style.outline = 'none';
                    e.target.style.borderColor = 'var(--color-accent)';
                    e.target.style.boxShadow = '0 0 0 2px var(--color-accent)';
                }}
                onBlur={(e) => {
                    e.target.style.borderColor = 'var(--color-border)';
                    e.target.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)';
                }}
            />
        </div>
    );
});
SearchInput.displayName = 'SearchInput';

// Memoized status filter component
const StatusFilter = memo(({
    value,
    onChange,
    allStatusLabel,
    publishedLabel,
    draftLabel
}: {
    value: string;
    onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
    allStatusLabel: string;
    publishedLabel: string;
    draftLabel: string;
}) => {
    return (
        <div className="relative w-full md:w-[240px]">
            <select
                value={value}
                onChange={onChange}
                style={{
                    width: '100%',
                    height: '48px',
                    backgroundColor: 'var(--color-surface)',
                    border: '1px solid var(--color-border)',
                    borderRadius: '12px',
                    paddingLeft: '24px',
                    paddingRight: '40px',
                    fontSize: '1.125rem',
                    color: 'var(--color-text)',
                    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                    transition: 'all 200ms',
                    appearance: 'none',
                }}
                onFocus={(e) => {
                    e.target.style.outline = 'none';
                    e.target.style.borderColor = 'var(--color-accent)';
                    e.target.style.boxShadow = '0 0 0 2px var(--color-accent)';
                }}
                onBlur={(e) => {
                    e.target.style.borderColor = 'var(--color-border)';
                    e.target.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)';
                }}
            >
                {React.createElement('option', { value: 'all' }, allStatusLabel)}
                {React.createElement('option', { value: 'published' }, publishedLabel)}
                {React.createElement('option', { value: 'draft' }, draftLabel)}
            </select>
            <ChevronDown 
                className="absolute right-4 top-1/2 -translate-y-1/2 h-5 w-5 pointer-events-none" 
                style={{ color: 'var(--color-text-muted)' }}
            />
        </div>
    );
});
StatusFilter.displayName = 'StatusFilter';

interface StatusControlProps {
    status: string;
    onActivate: (active: boolean) => void;
    botId: string;
    isLoading: boolean;
}

const StatusControl = memo(({ status, onActivate, botId, isLoading, t }: StatusControlProps & { t: any }) => {
    const isPublished = status === 'published';

    return (
        <div className="flex items-center gap-2 justify-end">
            <span style={{
                padding: '4px 12px',
                borderRadius: '9999px',
                fontSize: '0.875rem',
                fontWeight: '500',
                minWidth: '70px',
                textAlign: 'center',
                backgroundColor: isPublished ? 'var(--bot-list-status-published-bg)' : 'var(--bot-list-status-draft-bg)',
                color: isPublished ? 'var(--bot-list-status-published-text)' : 'var(--bot-list-status-draft-text)',
            }}>
                {isPublished ? t('agents.statusPublished') : t('agents.statusDraft')}
            </span>
            <div
                style={{
                    position: 'relative',
                    display: 'inline-flex',
                    height: '24px',
                    width: '44px',
                    flexShrink: 0,
                    cursor: isLoading ? 'not-allowed' : 'pointer',
                    borderRadius: '9999px',
                    transition: 'colors 200ms ease-in-out',
                    backgroundColor: isPublished ? 'var(--bot-list-toggle-on)' : 'var(--bot-list-toggle-off)',
                    opacity: isLoading ? 0.5 : 1,
                }}
                onClick={(e) => {
                    e.stopPropagation();
                    if (!isLoading) {
                        onActivate(!isPublished);
                    }
                }}
            >
                {isLoading ? (
                    <div className="absolute inset-0 flex items-center justify-center">
                        <div 
                            className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"
                            style={{ borderColor: 'var(--bot-list-toggle-thumb)' }}
                        />
                    </div>
                ) : (
                    <span
                        style={{
                            pointerEvents: 'none',
                            display: 'inline-block',
                            height: '20px',
                            width: '20px',
                            transform: isPublished ? 'translateX(20px)' : 'translateX(2px)',
                            borderRadius: '9999px',
                            backgroundColor: 'var(--bot-list-toggle-thumb)',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                            transition: 'transform 200ms ease-in-out',
                            margin: '2px 0',
                        }}
                    />
                )}
            </div>
        </div>
    );
});
StatusControl.displayName = 'StatusControl';

/**
 * #184 (A1/A2): "published ≠ live". A published agent receives inbound messages only while a
 * WhatsApp engine is online — the extension, a Chrome gateway or a Baileys pod; every online
 * engine captures inbound (plan A.9.5). "Live — listening" vs "Engine not running — won't
 * reply". Rendered only for published agents.
 */
const AgentReachabilityBadge = memo(({ isPublished, t }: { isPublished: boolean; t: any }) => {
    const isListening = useAppSelector(selectOnlineEngines).length > 0;
    if (!isPublished) return null;
    const liveText = isListening ? 'var(--bot-reach-live-text, #047857)' : 'var(--bot-reach-off-text, #b45309)';
    return (
        <div
            className="flex items-center gap-2 mt-3"
            role="status"
            aria-live="polite"
            style={{
                padding: '8px 12px',
                borderRadius: '10px',
                backgroundColor: isListening ? 'var(--bot-reach-live-bg, rgba(16,185,129,0.12))' : 'var(--bot-reach-off-bg, rgba(245,158,11,0.14))',
                border: `1px solid ${isListening ? 'var(--bot-reach-live-border, rgba(16,185,129,0.35))' : 'var(--bot-reach-off-border, rgba(245,158,11,0.4))'}`,
            }}
            title={isListening
                ? t('agents.reachLiveHint', { defaultValue: 'A WhatsApp engine is online, so this agent receives and replies to messages.' })
                : t('agents.reachOffHint', { defaultValue: 'This agent is published, but no WhatsApp engine is online — it will not receive or reply to messages until you connect WhatsApp (open WhatsApp Web with the Blueticks extension, or connect a server gateway).' })}
        >
            {isListening
                ? <Wifi className="w-4 h-4" style={{ color: liveText, flexShrink: 0 }} />
                : <WifiOff className="w-4 h-4" style={{ color: liveText, flexShrink: 0 }} />}
            <span style={{ fontSize: '0.8125rem', fontWeight: 500, color: liveText }}>
                {isListening
                    ? t('agents.reachLive', { defaultValue: 'Live — listening for messages' })
                    : t('agents.reachOff', { defaultValue: "Engine not running — won't reply" })}
            </span>
        </div>
    );
});
AgentReachabilityBadge.displayName = 'AgentReachabilityBadge';

// Delete confirmation dialog component
const DeleteConfirmationDialog = memo(({
    isOpen,
    bot,
    onClose,
    onConfirm
}: {
    isOpen: boolean;
    bot: Bot | null;
    onClose: () => void;
    onConfirm: () => void;
}) => {
    if (!isOpen || !bot) return null;

    return (
        <div
            style={{ 
                zIndex: 1201,
                position: 'fixed',
                inset: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <div style={{
                backgroundColor: 'var(--color-surface)',
                borderRadius: '8px',
                padding: '24px',
                maxWidth: '448px',
                width: '100%',
                margin: '0 16px',
            }}>
                <h3 style={{
                    fontSize: '1.125rem',
                    fontWeight: '600',
                    marginBottom: '8px',
                    color: 'var(--color-text)',
                }}>
                    Delete Bot
                </h3>
                <p style={{
                    color: 'var(--color-text-muted)',
                    marginBottom: '16px',
                }}>
                    Are you sure you want to delete "{bot.name}"? This action cannot be undone.
                </p>
                <div className="flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        style={{
                            padding: '8px 16px',
                            color: 'var(--color-text-muted)',
                            transition: 'color 200ms',
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.color = 'var(--color-text)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.color = 'var(--color-text-muted)';
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        style={{
                            padding: '8px 16px',
                            backgroundColor: 'var(--color-danger)',
                            color: 'white',
                            borderRadius: '4px',
                            transition: 'background-color 200ms',
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'var(--color-danger)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'var(--color-danger)';
                        }}
                    >
                        Delete
                    </button>
                </div>
            </div>
        </div>
    );
});
DeleteConfirmationDialog.displayName = 'DeleteConfirmationDialog';

const BotListContent = () => {
    const { t } = useTranslation(namespace);
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [botToDelete, setBotToDelete] = useState<Bot | null>(null);
    const [togglingBots, setTogglingBots] = useState<Set<string>>(new Set());
    const [isAddCreditsModalOpen, setIsAddCreditsModalOpen] = useState(false);
    const [calculateBotStats] = useCalculateBotStatsMutation();
    const analytics = new AnalyticsService();

    // Credit usage selectors
    const creditsData = useAppSelector(selectCreditsData);
    const creditUsageIsLoading = useAppSelector(selectCreditUsageIsLoading);

    const allBots: Bot[] = useAppSelector((state) => botSelectByQuery(state.DBBotReducer, {}));
    const isLoading = useAppSelector(state => state.DBBotReducer.itemsLoading);

    useEffect(() => {
        dispatch(DBBotThunks.find({ $paginate: false }));
        dispatch(DBBotDeploymentThunks.find({ $paginate: false }));
    }, [dispatch]);

    useEffect(() => {
        if (!isLoading && allBots.length > 0) {
            const botIds = allBots.map(bot => bot._id).filter((id): id is string => typeof id === 'string');
            calculateBotStats(botIds).catch(error =>
                console.error('Failed to calculate bot stats:', error)
            );
        }
    }, [isLoading, allBots, calculateBotStats]);

    // Fetch credit usage data on component mount
    useEffect(() => {
        dispatch(fetchCreditUsage('billing_period'));
    }, [dispatch]);

    const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const searchValue = e.target.value;
        setSearchQuery(searchValue);
        // Removed search tracking - too frequent and not actionable
    }, []);

    const handleStatusFilterChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
        const filterValue = e.target.value;
        setStatusFilter(filterValue);
        // Removed filter tracking - too granular and not very actionable
    }, []);

    const handleAddCredits = useCallback(() => {
        setIsAddCreditsModalOpen(true);
    }, []);

    const handleBotActivation = useCallback(async (botId: string, active: boolean) => {
        const bot = allBots.find(b => b._id === botId);
        if (!bot) return;

        setTogglingBots(prev => new Set(prev).add(botId));
        try {
            await dispatch(toggleBotActivationThunk({
                bot,
                shouldActivate: active,
                findLatestDeployment: active
            }));
            
            // Track successful activation/deactivation
            analytics.event('bot_activation_success', {
                category: 'bot_management',
                action: active ? 'bot_activated' : 'bot_deactivated',
                bot_id: botId,
                bot_name: bot.name
            });
        } catch (error) {
            console.error('Failed to toggle bot activation:', error);
            
            // Track activation failure
            analytics.event('bot_activation_error', {
                category: 'bot_management',
                action: 'activation_failed',
                bot_id: botId,
                bot_name: bot.name,
                target_status: active ? 'activate' : 'deactivate',
                error: error instanceof Error ? error.message : 'Unknown error'
            });
        } finally {
            setTogglingBots(prev => {
                const next = new Set(prev);
                next.delete(botId);
                return next;
            });
        }
    }, [allBots, dispatch, analytics]);

    const handleDeleteClick = useCallback((e: React.MouseEvent, bot: Bot) => {
        e.stopPropagation();
        
        // Track delete attempt
        analytics.event('bot_delete_initiated', {
            category: 'bot_management',
            action: 'delete_dialog_opened',
            bot_id: bot._id,
            bot_name: bot.name,
            bot_status: bot.activeDeploymentId ? 'published' : 'draft'
        });
        
        setBotToDelete(bot);
        setIsDeleteDialogOpen(true);
    }, [analytics]);

    const handleDeleteConfirm = useCallback(async () => {
        if (!botToDelete) return;

        const botToDeleteCopy = botToDelete;
        
        // Track delete confirmation
        analytics.event('bot_delete_confirmed', {
            category: 'bot_management',
            action: 'delete_confirmed',
            bot_id: botToDeleteCopy._id,
            bot_name: botToDeleteCopy.name,
            bot_status: botToDeleteCopy.activeDeploymentId ? 'published' : 'draft'
        });
        
        setBotToDelete(null);
        setIsDeleteDialogOpen(false);

        try {
            await dispatch(DBBotThunks.delete({ entity: botToDeleteCopy }));
            await dispatch(setCurrentBot(null));
            
            // Track successful deletion
            analytics.event('bot_delete_success', {
                category: 'bot_management',
                action: 'delete_completed',
                bot_id: botToDeleteCopy._id,
                bot_name: botToDeleteCopy.name,
                bot_status: botToDeleteCopy.activeDeploymentId ? 'published' : 'draft'
            });
        } catch (error) {
            console.error('Failed to delete bot:', error);
            
            // Track deletion failure
            analytics.event('bot_delete_error', {
                category: 'bot_management',
                action: 'delete_failed',
                bot_id: botToDeleteCopy._id,
                bot_name: botToDeleteCopy.name,
                error: error instanceof Error ? error.message : 'Unknown error'
            });
        }
    }, [botToDelete, dispatch, analytics]);

    const handleBotClick = useCallback(async (bot: Bot) => {
        // Track bot detail view
        analytics.event('bot_detail_viewed', {
            category: 'bot_navigation',
            action: 'view_bot_details',
            bot_id: bot._id,
            bot_name: bot.name,
            bot_status: bot.activeDeploymentId ? 'published' : 'draft'
        });
        
        // Clear all entities from both reducers using their dedicated reset actions
        await dispatch(resetBotDeploymentState());
        await dispatch(resetAutomationSessionState());

        navigate(`/bot-detail-view/${bot._id}`);
    }, [navigate, dispatch, analytics]);

    const handleEditClick = useCallback(async (e: React.MouseEvent, bot: Bot) => {
        e.stopPropagation(); // Prevent navigation to detail view
        
        // Track bot edit
        analytics.event('bot_edit_initiated', {
            category: 'bot_navigation',
            action: 'edit_bot_started',
            bot_id: bot._id,
            bot_name: bot.name,
            bot_status: bot.activeDeploymentId ? 'published' : 'draft'
        });
        
        if (bot?._id) {
            await dispatch(setIsReadyToLoadFlow(false));
            await navigate(`/bot-build-view/${bot._id}`);
        }
    }, [navigate, analytics]);



    const filteredBots = allBots.filter(bot => {
        const matchesSearch = bot.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            bot.description?.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesStatus = statusFilter === 'all' ||
            (statusFilter === 'published' && bot.activeDeploymentId) ||
            (statusFilter === 'draft' && !bot.activeDeploymentId);
        return matchesSearch && matchesStatus;
    }).sort((a, b) => {
        const aActive = a.activeDeploymentId ? 1 : 0;
        const bActive = b.activeDeploymentId ? 1 : 0;
        if (aActive !== bActive) return bActive - aActive;
        const aUpdated = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
        const bUpdated = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
        return bUpdated - aUpdated;
    });

    return (
        <>
            {/* Add Credits Modal */}
            <AddCreditsModal 
                open={isAddCreditsModalOpen} 
                onOpenChange={setIsAddCreditsModalOpen} 
            />

            <div
                className="h-full overflow-auto p-4 md:p-12"
                style={{ backgroundColor: 'var(--color-bg)' }}
            >
                <div className="max-w-[1600px] mx-auto space-y-6 md:space-y-12">
                    {/* Credit Warning Alert */}

                    {/* Credit Warning Alert */}
                    <CreditWarningAlert
                        creditsRemaining={getValueOrDefault(creditsData, 'creditsRemaining', 1)}
                        percentageUsed={getValueOrDefault(creditsData, 'percentageUsed', 0)}
                        isLoading={creditUsageIsLoading}
                        onAddCredits={handleAddCredits}
                        className="mx-auto max-w-[1600px]"
                    />

                    {/* Filters */}
                    <div className="flex flex-col md:flex-row md:justify-between gap-2">
                        <SearchInput value={searchQuery} onChange={handleSearchChange} placeholder={t('agents.searchPlaceholder')} />
                        <StatusFilter 
                            value={statusFilter} 
                            onChange={handleStatusFilterChange}
                            allStatusLabel={t('agents.allStatus')}
                            publishedLabel={t('agents.statusPublished')}
                            draftLabel={t('agents.statusDraft')}
                        />
                    </div>

                    {/* Loading State */}
                    {isLoading && (
                        <div className="flex flex-col justify-center items-center min-h-[400px] py-20">
                            <div style={{
                                backgroundColor: 'var(--color-surface)',
                                borderRadius: '9999px',
                                padding: '24px',
                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                                marginBottom: '16px',
                            }}>
                                <Loader2 
                                    className="w-10 h-10 animate-spin" 
                                    style={{ color: 'var(--color-primary)' }}
                                />
                            </div>
                            <p style={{
                                color: 'var(--color-text-muted)',
                                fontWeight: '500',
                                fontSize: '1.125rem',
                                animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
                            }}>
                                {t('agents.loading')}
                            </p>
                        </div>
                    )}

                    {/* Empty State */}
                    {!isLoading && filteredBots.length === 0 && (
                        <div className="flex justify-center items-center min-h-[400px]">
                            <p style={{ color: 'var(--color-text-muted)' }}>
                                {t('agents.emptyState')}
                            </p>
                        </div>
                    )}

                    {/* Bot Grid */}
                    {!isLoading && filteredBots.length > 0 && (
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
                            {filteredBots.map((bot) => {
                                const stats = bot.botStats || {
                                    activeSessions: '--',
                                    todaySessions: '--',
                                    avgDuration: '--',
                                    latestDeployment: null
                                };
                                const isPublished = !!bot.activeDeploymentId;

                                // Format the deployment date with graceful handling
                                let deploymentDate = t('agents.never');
                                if (stats.latestDeployment?.createdAt) {
                                    const date = new Date(stats.latestDeployment.createdAt);
                                    if (!isNaN(date.getTime())) {
                                        deploymentDate = date.toLocaleDateString(undefined, {
                                            year: 'numeric',
                                            month: 'short',
                                            day: 'numeric'
                                        });
                                    }
                                }

                                return (
                                    <div
                                        key={bot._id}
                                        onClick={() => handleBotClick(bot)}
                                        style={{
                                            backgroundColor: 'var(--color-surface)',
                                            boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                                            transition: 'all 200ms',
                                            borderRadius: '16px',
                                            overflow: 'hidden',
                                            border: '1px solid var(--color-border)',
                                            cursor: 'pointer',
                                            position: 'relative',
                                        }}
                                        className="group"
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)';
                                        }}
                                    >
                                        <div className="p-5">
                                            {/* Header with bot info and controls */}
                                            <div className="flex items-center gap-3">
                                                <div style={{
                                                    padding: '8px',
                                                    backgroundColor: 'var(--color-surface-accent)',
                                                    borderRadius: '10px',
                                                    flexShrink: 0,
                                                }}>
                                                    <BotIcon
                                                        className="w-5 h-5"
                                                        style={{ color: 'var(--color-primary)' }}
                                                    />
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <div style={{
                                                        fontSize: '1rem',
                                                        fontWeight: '500',
                                                        color: 'var(--color-text)',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    }}>
                                                        {bot.name || 'New Chatbot (unsaved)'}
                                                    </div>
                                                    {bot.description && (
                                                        <p style={{
                                                            fontSize: '0.8125rem',
                                                            color: 'var(--color-text-muted)',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                        }}>
                                                            {bot.description}
                                                        </p>
                                                    )}
                                                </div>

                                                <StatusControl
                                                    status={isPublished ? 'published' : 'draft'}
                                                    onActivate={(active) => handleBotActivation(bot._id ?? '', active)}
                                                    botId={bot._id ?? ''}
                                                    isLoading={togglingBots.has(bot._id ?? '')}
                                                    t={t}
                                                />
                                            </div>

                                            {/* #184 A1: clear "live & listening" vs "won't reply" signal on published agents */}
                                            <AgentReachabilityBadge isPublished={isPublished} t={t} />

                                            <div className="flex items-center justify-between gap-4 mt-4 pt-4" style={{
                                                borderTop: '1px solid var(--color-border)',
                                            }}>
                                                <div className="flex items-center gap-2">
                                                    <Calendar
                                                        className="w-4 h-4"
                                                        style={{ color: 'var(--color-text-muted)' }}
                                                    />
                                                    <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)' }}>
                                                        {t('agents.todaySessions')}
                                                    </span>
                                                    <span style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--color-text)' }}>
                                                        {stats.todaySessions !== undefined ? stats.todaySessions : '--'}
                                                    </span>
                                                </div>
                                                <button
                                                    onClick={(e) => handleDeleteClick(e, bot)}
                                                    style={{
                                                        color: 'var(--color-text-muted)',
                                                        transition: 'all 200ms',
                                                        flexShrink: 0,
                                                        padding: '4px',
                                                    }}
                                                    title={t('agents.deleteBot')}
                                                    onMouseEnter={(e) => {
                                                        e.currentTarget.style.color = 'var(--color-danger)';
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.currentTarget.style.color = 'var(--color-text-muted)';
                                                    }}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            <DeleteConfirmationDialog
                isOpen={isDeleteDialogOpen}
                bot={botToDelete}
                onClose={() => {
                    setIsDeleteDialogOpen(false);
                    setBotToDelete(null);
                }}
                onConfirm={handleDeleteConfirm}
            />
        </>
    );
};

export default memo(BotListContent); 